import React, { createContext, useContext, useState, useCallback } from 'react';
import { Language, translations } from '@/i18n/translations';
import { CurrencyCode, formatPrice, convertPrice, getCurrency } from '@/i18n/currencies';

interface SettingsContextType {
  language: Language;
  currency: CurrencyCode;
  setLanguage: (lang: Language) => void;
  setCurrency: (currency: CurrencyCode) => void;
  t: (key: string) => string;
  formatAmount: (amountInDZD: number) => string;
  convertAmount: (amountInDZD: number) => number;
  currencySymbol: string;
  isRTL: boolean;
}

const defaultSettings: SettingsContextType = {
  language: 'fr',
  currency: 'DZD',
  setLanguage: () => {},
  setCurrency: () => {},
  t: (key: string) => key,
  formatAmount: (n: number) => `${n} DA`,
  convertAmount: (n: number) => n,
  currencySymbol: 'DA',
  isRTL: false,
};

const SettingsContext = createContext<SettingsContextType>(defaultSettings);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    return (localStorage.getItem('app_language') as Language) || 'fr';
  });
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    return (localStorage.getItem('app_currency') as CurrencyCode) || 'DZD';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app_language', lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, []);

  const setCurrency = useCallback((c: CurrencyCode) => {
    setCurrencyState(c);
    localStorage.setItem('app_currency', c);
  }, []);

  const t = useCallback((key: string): string => {
    return translations[language]?.[key] || translations['fr'][key] || key;
  }, [language]);

  const formatAmount = useCallback((amountInDZD: number): string => {
    return formatPrice(amountInDZD, currency);
  }, [currency]);

  const convertAmount = useCallback((amountInDZD: number): number => {
    return convertPrice(amountInDZD, currency);
  }, [currency]);

  const currencySymbol = getCurrency(currency).symbol;
  const isRTL = language === 'ar';

  // Set initial dir
  React.useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  return (
    <SettingsContext.Provider value={{ language, currency, setLanguage, setCurrency, t, formatAmount, convertAmount, currencySymbol, isRTL }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  return useContext(SettingsContext);
};
